import { Fragment, type ReactNode } from "react";
import {
  evaluateVisibility,
  getByPath,
  resolveElementProps,
  resolveRepeatItemStatePath,
  resolveRepeatStatePath,
  type Spec,
  type StateModel,
  type UIElement,
  type VisibilityContext
} from "@json-render/core";
import type { AgentChatSpec } from "./catalog";
import { agentChatRegistry } from "./registry";
import { getSpecPrimaryComponentType, getSpecPrimaryElementKey } from "./specOverrides";

type AgentChatRendererProps = {
  spec: AgentChatSpec | null;
};

type RenderContext = VisibilityContext & {
  repeatBasePath?: string;
};

const LIST_COMPONENT_TYPES = new Set(["RecordList", "NoteList", "InteractionList", "JobPostList", "EventList"]);

function renderChildren(spec: Spec, element: UIElement, ctx: RenderContext): ReactNode {
  if (element.repeat) {
    const statePath = resolveRepeatStatePath(element.repeat.statePath, ctx.repeatBasePath ?? null);
    if (!statePath) {
      return null;
    }

    const items = getByPath(ctx.stateModel, statePath);
    if (!Array.isArray(items) || items.length === 0) {
      return null;
    }

    return items.map((item, index) => {
      const repeatKey = element.repeat?.key;
      const itemRecord = item && typeof item === "object" ? (item as Record<string, unknown>) : null;
      const stableKey = repeatKey && itemRecord && repeatKey in itemRecord ? String(itemRecord[repeatKey]) : `${index}`;

      const itemCtx: RenderContext = {
        stateModel: ctx.stateModel,
        repeatItem: item,
        repeatIndex: index,
        repeatBasePath: resolveRepeatItemStatePath(statePath, index)
      };

      return (
        <Fragment key={stableKey}>
          {element.children?.map((childKey) => (
            <Fragment key={`${stableKey}-${childKey}`}>{renderElement(spec, childKey, itemCtx)}</Fragment>
          ))}
        </Fragment>
      );
    });
  }

  return element.children?.map((childKey) => <Fragment key={childKey}>{renderElement(spec, childKey, ctx)}</Fragment>);
}

function renderElement(spec: Spec, key: string, ctx: RenderContext): ReactNode {
  const element = spec.elements?.[key] as UIElement | undefined;
  if (!element) {
    return null;
  }

  if (!evaluateVisibility(element.visible, ctx)) {
    return null;
  }

  const Component = agentChatRegistry[element.type];
  if (!Component) {
    return null;
  }

  const resolvedProps = resolveElementProps((element.props ?? {}) as Record<string, unknown>, ctx);
  const children = renderChildren(spec, element, ctx);

  return <Component {...resolvedProps}>{children}</Component>;
}

/**
 * True when the primary list still has unresolved `$state` / non-array items (missing /state patches).
 * An explicit empty array (`items: []`) is renderable so AgentList can show its emptyLabel.
 */
export function specNeedsContentFallback(spec: Spec | null, stateModel: StateModel = spec?.state ?? {}): boolean {
  if (!spec?.elements) {
    return false;
  }

  const primaryKey = getSpecPrimaryElementKey(spec);
  const primaryType = getSpecPrimaryComponentType(spec);
  if (!primaryKey || !primaryType || !LIST_COMPONENT_TYPES.has(primaryType)) {
    return false;
  }

  const ctx: RenderContext = { stateModel };
  const element = spec.elements[primaryKey] as UIElement | undefined;
  if (!element) {
    return false;
  }

  const props = resolveElementProps((element.props ?? {}) as Record<string, unknown>, ctx);
  const items = props.items;
  // Explicit [] is intentional empty state; only unresolved / non-array items need fallback.
  if (!Array.isArray(items)) {
    return true;
  }

  if (element.repeat) {
    const statePath = resolveRepeatStatePath(element.repeat.statePath, ctx.repeatBasePath ?? null);
    if (!statePath) {
      return true;
    }

    const repeatItems = getByPath(ctx.stateModel, statePath);
    if (!Array.isArray(repeatItems)) {
      return true;
    }
  }

  return false;
}

export default function AgentChatRenderer({ spec }: AgentChatRendererProps) {
  if (!spec?.root) {
    return null;
  }

  const stateModel: StateModel = spec.state ?? {};
  const ctx: RenderContext = { stateModel };

  return <div className="agent-chat-spec">{renderElement(spec, spec.root, ctx)}</div>;
}
